// SWITCHING TO REAL DATA:
// When ready to use live ESPN data instead of mock:
// 1. Import getRealTeamStats from '../lib/realStats'
// 2. See realStats.js for full instructions
// 3. The component structure stays exactly the same
// 4. Only the data source changes

import { useState, useEffect } from 'react'
import { fighterInitial } from '../lib/teamColors'
import { getTeamStats, getH2H, getStatDefs, fetchUFCFighterStats } from '../lib/teamStats'
import { log } from '../lib/log'

const TEAM_NAME_MAP = {
  // NBA
  'Oklahoma City Thunder':      'OKC Thunder',
  'Los Angeles Lakers':         'LA Lakers',
  'Los Angeles Clippers':       'LA Clippers',
  'Minnesota Timberwolves':     'Minnesota Timberwolves',
  'Golden State Warriors':      'Golden State Warriors',
  'Dallas Mavericks':           'Dallas Mavericks',
  'Cleveland Cavaliers':        'Cleveland Cavaliers',
  'Orlando Magic':              'Orlando Magic',
  'Atlanta Hawks':              'Atlanta Hawks',
  'Phoenix Suns':               'Phoenix Suns',
  // MLB
  'Los Angeles Dodgers':        'LA Dodgers',
  'San Francisco Giants':       'SF Giants',
  'New York Yankees':           'New York Yankees',
  'Boston Red Sox':             'Boston Red Sox',
  // NHL
  'Vegas Golden Knights':       'Vegas Golden Knights',
  'Colorado Avalanche':         'Colorado Avalanche',
  'Edmonton Oilers':            'Edmonton Oilers',
  'Florida Panthers':           'Florida Panthers',
  'Carolina Hurricanes':        'Carolina Hurricanes',
  'New York Rangers':           'New York Rangers',
  'Toronto Maple Leafs':        'Toronto Maple Leafs',
  'Tampa Bay Lightning':        'Tampa Bay Lightning',
  // NFL
  'San Francisco 49ers':        'San Francisco 49ers',
  'Los Angeles Rams':           'Los Angeles Rams',
  'Los Angeles Chargers':       'Los Angeles Chargers',
}
function normalizeTeamName(name) {
  return TEAM_NAME_MAP[name] || name
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function overallRec(homeRec, awayRec) {
  if (!homeRec || !awayRec) return homeRec || awayRec || ''
  try {
    const hp = homeRec.split('-').map(Number)
    const ap = awayRec.split('-').map(Number)
    if (hp.some(isNaN) || ap.some(isNaN)) return homeRec
    if (hp.length === 3 && ap.length === 3)
      return `${hp[0]+ap[0]}-${hp[1]+ap[1]}-${hp[2]+ap[2]}`
    return `${hp[0]+ap[0]}-${hp[1]+ap[1]}`
  } catch { return homeRec }
}

function displayVal(val) {
  if (val === null || val === undefined) return 'N/A'
  if (typeof val === 'number' && isNaN(val)) return 'N/A'
  if (val === 'NaN' || val === 'undefined') return 'N/A'
  return val
}

function safeWidth(val) {
  const n = parseFloat(val)
  return isNaN(n) ? 0 : n
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function SectionLabel({ text }) {
  return (
    <div style={{
      fontSize:'10px', letterSpacing:'1.5px', color:'var(--text-muted)',
      fontFamily:'Arial,sans-serif', marginBottom:'10px', paddingLeft:'2px',
    }}>
      {text}
    </div>
  )
}

function Card({ children, style = {} }) {
  return (
    <div style={{
      background:'rgba(255,255,255,0.03)',
      border:'1px solid rgba(255,255,255,0.06)',
      borderRadius:'10px',
      overflow:'hidden',
      ...style,
    }}>
      {children}
    </div>
  )
}

function FormPill({ result }) {
  const win = result === 'W'
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', justifyContent:'center',
      width:'22px', height:'22px', borderRadius:'6px',
      fontSize:'11px', fontWeight:'bold', fontFamily:'Arial,sans-serif', flexShrink:0,
      background: win ? 'rgba(46,204,113,0.18)' : 'rgba(255,59,59,0.18)',
      color:       win ? 'var(--green)' : 'var(--red)',
      border:      `1px solid ${win ? 'rgba(46,204,113,0.35)' : 'rgba(255,59,59,0.3)'}`,
    }}>
      {result}
    </span>
  )
}

function InjuryBadge({ status }) {
  const cfg = {
    Out:          { bg:'rgba(255,59,59,0.18)',  color:'var(--red)',   border:'rgba(255,59,59,0.45)'  },
    Questionable: { bg:'rgba(0,230,118,0.18)',color:'var(--gold)',  border:'rgba(0,230,118,0.45)'},
    Probable:     { bg:'rgba(46,204,113,0.12)', color:'var(--green)', border:'rgba(46,204,113,0.35)' },
  }[status] || { bg:'rgba(255,255,255,0.06)', color:'var(--text-dim)', border:'rgba(255,255,255,0.1)' }
  return (
    <span style={{
      fontSize:'10px', fontFamily:'Arial,sans-serif', fontWeight:'bold',
      padding:'3px 7px', borderRadius:'5px', flexShrink:0, whiteSpace:'nowrap',
      background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.border}`,
    }}>
      {status === 'Questionable' ? 'Q' : status.toUpperCase()}
    </span>
  )
}

// ── UFC fighter header (UFC-only replacement for TeamHeader) ─────────────────
// Renders headshot + name + record on each side, with height/weight/reach/
// stance stacked below. None of the team-sport sections (form, h2h, injuries)
// apply to MMA so we skip those for UFC.

function FighterInfoRow({ label, value }) {
  if (!value) return null
  return (
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text-dim)', fontFamily:'Arial,sans-serif', padding:'2px 0' }}>
      <span style={{ color:'var(--text-muted)' }}>{label}</span>
      <span style={{ color:'var(--text)' }}>{value}</span>
    </div>
  )
}

function FighterSide({ fighter, align }) {
  const isRight = align === 'right'
  // Initial-letter chip — owned UI in place of the prior ESPN-CDN headshot.
  // Shares the fighterInitial derivation with TeamLogoImg and PickemMode so
  // every UFC chip across the app renders the same letter for the same name.
  const initial = fighterInitial(fighter.name)
  return (
    <div style={{ minWidth:0, textAlign:isRight ? 'right' : 'left' }}>
      <div style={{
        width:54, height:54, borderRadius:'50%',
        background:'var(--surface2)',
        color:'var(--text-dim)',
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:22, fontWeight:700, fontFamily:'Arial,sans-serif',
        marginBottom:6, marginLeft: isRight ? 'auto' : 0,
      }}>{initial}</div>
      {/* Name on its own line. Allowed to wrap inside the column rather than
          truncate-and-overflow as before. wordBreak:break-word breaks an
          unusually long single token (rare for fighter names; defensive). */}
      <div style={{
        fontSize:14, fontWeight:'bold',
        color: isRight ? 'var(--gold)' : 'var(--text)',
        fontFamily:'inherit',
        lineHeight:1.25,
        wordBreak:'break-word',
      }}>
        {fighter.name}
      </div>
      {/* Nickname on its own line below. Inherits parent's textAlign (right
          for the right side, left for the left side) so it lands under the
          corresponding edge of the name. Smaller, italic, dim — same visual
          weight as before, just on a new line. */}
      {fighter.nickname && (
        <div style={{
          fontSize:12, fontStyle:'italic', fontWeight:'normal',
          color:'var(--text-dim)',
          fontFamily:'inherit',
          marginTop:2,
          lineHeight:1.25,
          wordBreak:'break-word',
        }}>
          ({fighter.nickname})
        </div>
      )}
      {fighter.record && (
        <div style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'Arial,sans-serif', marginTop:2 }}>
          {fighter.record}
        </div>
      )}
      {fighter.nationality && (
        <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:4, justifyContent: isRight ? 'flex-end' : 'flex-start' }}>
          <span style={{ fontSize:10, color:'var(--text-muted)', fontFamily:'Arial,sans-serif' }}>
            {fighter.nationality}
          </span>
        </div>
      )}
      <div style={{ marginTop:8 }}>
        <FighterInfoRow label="Height" value={fighter.height} />
        <FighterInfoRow label="Weight" value={fighter.weight} />
        <FighterInfoRow label="Reach"  value={fighter.reach} />
        <FighterInfoRow label="Stance" value={fighter.stance} />
        {fighter.age && <FighterInfoRow label="Age" value={String(fighter.age)} />}
      </div>
    </div>
  )
}

function UFCFighterHeader({ awayS, homeS }) {
  return (
    <Card style={{ marginBottom:'16px', background:'linear-gradient(135deg,rgba(0,230,118,0.08),rgba(0,230,118,0.02))' }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 40px 1fr', alignItems:'flex-start', padding:'14px 16px', gap:'8px' }}>
        <FighterSide fighter={awayS} align="left" />
        <div style={{ textAlign:'center', paddingTop:18 }}>
          <div style={{ fontSize:11, fontWeight:'bold', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', letterSpacing:'1px' }}>VS</div>
        </div>
        <FighterSide fighter={homeS} align="right" />
      </div>
    </Card>
  )
}

// ── Section 1: Team Header ────────────────────────────────────────────────────

function TeamHeader({ game, awayS, homeS }) {
  const awayRec = awayS.record || overallRec(awayS.homeRecord, awayS.awayRecord)
  const homeRec = homeS.record || overallRec(homeS.homeRecord, homeS.awayRecord)

  return (
    <Card style={{ marginBottom:'16px', background:'linear-gradient(135deg,rgba(0,230,118,0.08),rgba(0,230,118,0.02))' }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 48px 1fr', alignItems:'center', padding:'14px 16px', gap:'8px' }}>
        {/* Away team */}
        <div style={{ minWidth:0 }}>
          <div style={{ fontSize:'14px', fontWeight:'bold', color:'var(--text)', fontFamily:'inherit', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', lineHeight:'1.3' }}>
            {game.away.team}
          </div>
          <div style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', marginTop:'3px' }}>
            {awayRec}
          </div>
          <div style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', marginTop:'1px', opacity:0.7 }}>
            Away: {awayS.awayRecord}
          </div>
        </div>

        {/* VS */}
        <div style={{ textAlign:'center', flexShrink:0 }}>
          <div style={{ fontSize:'11px', fontWeight:'bold', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', letterSpacing:'1px' }}>VS</div>
        </div>

        {/* Home team */}
        <div style={{ minWidth:0, textAlign:'right' }}>
          <div style={{ fontSize:'14px', fontWeight:'bold', color:'var(--gold)', fontFamily:'inherit', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', lineHeight:'1.3' }}>
            {game.home.team}
          </div>
          <div style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', marginTop:'3px' }}>
            {homeRec}
          </div>
          <div style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', marginTop:'1px', opacity:0.7 }}>
            Home: {homeS.homeRecord}
          </div>
        </div>
      </div>
    </Card>
  )
}

// ── Section 2: Recent Form ────────────────────────────────────────────────────

function FormRow({ abbr, stats, isHome }) {
  const streakWin = (stats.streak || '')[0] === 'W'
  return (
    <div style={{ padding:'12px 14px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
        <span style={{
          fontSize:'13px', fontWeight:'bold',
          color: isHome ? 'var(--gold)' : 'var(--text)',
          fontFamily:'Arial,sans-serif', width:'36px', flexShrink:0,
        }}>
          {abbr}
        </span>
        <div style={{ display:'flex', gap:'4px', flex:1 }}>
          {stats.form.map((r, i) => <FormPill key={i} result={r} />)}
        </div>
        <span style={{
          fontSize:'12px', fontWeight:'bold', flexShrink:0,
          color: streakWin ? 'var(--green)' : 'var(--red)',
          fontFamily:'Arial,sans-serif',
        }}>
          {stats.streak}
        </span>
      </div>
      <div style={{ paddingLeft:'44px', marginTop:'5px', fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif' }}>
        {isHome ? `Home ${stats.homeRecord}` : `Away ${stats.awayRecord}`}
      </div>
    </div>
  )
}

function RecentForm({ game, awayS, homeS }) {
  return (
    <div style={{ marginBottom:'18px' }}>
      <SectionLabel text="RECENT FORM" />
      <Card>
        <FormRow abbr={game.away.abbr} stats={awayS} isHome={false} />
        <div style={{ height:'1px', background:'rgba(255,255,255,0.06)', margin:'0 14px' }} />
        <FormRow abbr={game.home.abbr} stats={homeS} isHome={true} />
      </Card>
    </div>
  )
}

// ── Section 3: Team Stats ─────────────────────────────────────────────────────

function StatRow({ def, av, hv, index }) {
  if (av == null || hv == null) return null
  const an = parseFloat(av), hn = parseFloat(hv)
  if (isNaN(an) || isNaN(hn)) return null
  const awayBetter = def.better === 'higher' ? an > hn : an < hn
  const homeBetter = def.better === 'higher' ? hn > an : hn < an
  const offset = Math.min(an, hn, 0)
  const aw = an - offset + 0.01, hw = hn - offset + 0.01, total = aw + hw
  const awayFill = safeWidth(total === 0 ? 50 : def.better === 'lower' ? (hw / total) * 100 : (aw / total) * 100)

  let awayLabel, homeLabel
  try { awayLabel = def.fmt(an) } catch { awayLabel = displayVal(an) }
  try { homeLabel = def.fmt(hn) } catch { homeLabel = displayVal(hn) }

  return (
    <div style={{
      padding:'11px 14px',
      background: index % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
    }}>
      {/* Stat label centered */}
      <div style={{ textAlign:'center', fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', letterSpacing:'0.5px', marginBottom:'7px' }}>
        {def.label}
      </div>
      {/* Values + bar */}
      <div style={{ display:'grid', gridTemplateColumns:'52px 1fr 52px', alignItems:'center', gap:'8px' }}>
        <div style={{
          fontSize:'15px', fontFamily:'Arial,sans-serif', textAlign:'right',
          fontWeight: awayBetter ? 'bold' : 'normal',
          color: awayBetter ? 'var(--text)' : 'rgba(255,255,255,0.35)',
        }}>
          {awayLabel}
        </div>
        <div style={{ display:'flex', height:'7px', borderRadius:'4px', overflow:'hidden', background:'rgba(255,255,255,0.07)' }}>
          <div style={{ width:`${awayFill}%`, background: awayBetter ? 'var(--gold)' : 'rgba(120,120,140,0.35)', flexShrink:0, transition:'width .35s' }} />
          <div style={{ flex:1, background: homeBetter ? 'var(--gold)' : 'rgba(120,120,140,0.35)' }} />
        </div>
        <div style={{
          fontSize:'15px', fontFamily:'Arial,sans-serif', textAlign:'left',
          fontWeight: homeBetter ? 'bold' : 'normal',
          color: homeBetter ? 'var(--gold)' : 'rgba(255,255,255,0.35)',
        }}>
          {homeLabel}
        </div>
      </div>
    </div>
  )
}

function TeamStats({ game, awayS, homeS, statDefs }) {
  const rows = statDefs
    .map((def, i) => ({ def, av: awayS.stats[def.key], hv: homeS.stats[def.key], i }))
    .filter(({ av, hv }) => av != null && hv != null)

  if (rows.length === 0) return null

  return (
    <div style={{ marginBottom:'18px' }}>
      <SectionLabel text="TEAM STATS" />
      {/* Column headers */}
      <div style={{ display:'grid', gridTemplateColumns:'52px 1fr 52px', gap:'8px', marginBottom:'4px', padding:'0 14px' }}>
        <div style={{ textAlign:'right', fontSize:'11px', fontWeight:'bold', color:'var(--text)', fontFamily:'Arial,sans-serif' }}>{game.away.abbr}</div>
        <div />
        <div style={{ textAlign:'left', fontSize:'11px', fontWeight:'bold', color:'var(--gold)', fontFamily:'Arial,sans-serif' }}>{game.home.abbr}</div>
      </div>
      <Card>
        {rows.map(({ def, av, hv, i }) => (
          <StatRow key={def.key} def={def} av={av} hv={hv} index={i} />
        ))}
      </Card>
    </div>
  )
}

// ── Section 4: Head to Head ───────────────────────────────────────────────────

function HeadToHead({ h2h }) {
  return (
    <div style={{ marginBottom:'18px' }}>
      <SectionLabel text="HEAD TO HEAD" />
      {/* Summary card */}
      <Card style={{ padding:'12px 14px', marginBottom:'8px' }}>
        <div style={{ fontSize:'14px', fontWeight:'bold', color:'var(--gold)', fontFamily:'inherit', marginBottom:'3px' }}>
          {h2h.record}
        </div>
        <div style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif' }}>
          Avg score: {h2h.avgScore}
        </div>
      </Card>
      {/* Individual games */}
      {h2h.meetings.map((m, i) => (
        <Card key={i} style={{ padding:'11px 14px', marginBottom:'6px' }}>
          <div style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', marginBottom:'4px' }}>
            {m.date}
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'8px' }}>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:'13px', fontWeight:'bold', color:'var(--text)', fontFamily:'inherit', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {m.winner}
              </div>
            </div>
            <div style={{ fontSize:'14px', fontWeight:'bold', color:'var(--gold)', fontFamily:'Arial,sans-serif', flexShrink:0 }}>
              {m.score}
            </div>
          </div>
        </Card>
      ))}
    </div>
  )
}

// ── Section 5: Injury Report ──────────────────────────────────────────────────

function InjuryTeamSection({ abbr, injuries, isHome }) {
  return (
    <div style={{ marginBottom:'12px' }}>
      <div style={{
        fontSize:'11px', fontWeight:'bold', letterSpacing:'0.5px',
        color: isHome ? 'var(--gold)' : 'var(--text)',
        fontFamily:'Arial,sans-serif', marginBottom:'6px', paddingLeft:'2px',
      }}>
        {abbr}
      </div>
      {injuries.length === 0 ? (
        <div style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', padding:'8px 14px', fontStyle:'italic' }}>
          No injuries reported
        </div>
      ) : (
        injuries.slice(0, 4).map((inj, i) => (
          <Card key={i} style={{ marginBottom:'5px', padding:'10px 14px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'10px' }}>
              <div style={{ minWidth:0, flex:1 }}>
                <div style={{ fontSize:'13px', fontWeight:'bold', color:'var(--text)', fontFamily:'inherit', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {inj.player}
                </div>
                <div style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', marginTop:'2px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {inj.note}
                </div>
              </div>
              <InjuryBadge status={inj.status} />
            </div>
          </Card>
        ))
      )}
    </div>
  )
}

function InjuryReport({ game, awayS, homeS }) {
  const hasInjuries = awayS.injuries.length > 0 || homeS.injuries.length > 0
  return (
    <div style={{ marginBottom:'4px' }}>
      <SectionLabel text="INJURY REPORT" />
      <InjuryTeamSection abbr={game.away.abbr} injuries={awayS.injuries} isHome={false} />
      <InjuryTeamSection abbr={game.home.abbr} injuries={homeS.injuries} isHome={true} />
    </div>
  )
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function TeamComparison({ game }) {
  const [awayS, setAwayS]   = useState(null)
  const [homeS, setHomeS]   = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      let away, home
      if (game.sport === 'UFC') {
        log('[TeamComparison] UFC fetch:', {
          homeAthleteId: game.homeAthleteId,
          awayAthleteId: game.awayAthleteId,
          home: game.home?.team,
          away: game.away?.team,
        })
        ;[away, home] = await Promise.all([
          fetchUFCFighterStats(game.awayAthleteId, normalizeTeamName(game.away.team)),
          fetchUFCFighterStats(game.homeAthleteId, normalizeTeamName(game.home.team)),
        ])
      } else {
        ;[away, home] = await Promise.all([
          getTeamStats(normalizeTeamName(game.away.team), game.sport),
          getTeamStats(normalizeTeamName(game.home.team), game.sport),
        ])
      }
      if (!cancelled) { setAwayS(away); setHomeS(home); setLoading(false) }
    }
    load()
    return () => { cancelled = true }
  }, [game.away.team, game.home.team, game.sport, game.homeAthleteId, game.awayAthleteId])

  const h2h      = getH2H(game.away.team, game.home.team)
  const statDefs = getStatDefs(game.sport)

  if (loading) return (
    <div style={{ padding:'20px 0', textAlign:'center' }}>
      <div style={{ width:'20px', height:'20px', border:'2px solid var(--border)', borderTopColor:'var(--gold)', borderRadius:'50%', margin:'0 auto 8px', animation:'spin .8s linear infinite' }} />
      <div style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif' }}>Loading stats…</div>
    </div>
  )

  if (!awayS && !homeS) {
    return (
      <div style={{ padding:'20px 0', textAlign:'center' }}>
        <div style={{ fontSize:'13px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif' }}>
          Team stats are not available for this matchup
        </div>
      </div>
    )
  }
  if (!awayS || !homeS) {
    const missing = !awayS ? game.away.team : game.home.team
    return (
      <div style={{ padding:'20px 0', textAlign:'center' }}>
        <div style={{ fontSize:'13px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif' }}>
          Stats unavailable for {missing}
        </div>
      </div>
    )
  }

  const isUFC = game.sport === 'UFC'
  // ESPN's statistics $ref doesn't always return numeric data (404 or empty
  // payload). When that happens for UFC, hide the empty stat grid and show
  // a "coming soon" line so users see the bio info without a phantom section.
  const ufcHasStats = isUFC && awayS && homeS
    ? Object.values(awayS.stats || {}).some(v => v != null)
      || Object.values(homeS.stats || {}).some(v => v != null)
    : false

  return (
    <div style={{ width:'100%', boxSizing:'border-box', overflow:'hidden', paddingTop:'14px' }}>
      {isUFC
        ? <UFCFighterHeader awayS={awayS} homeS={homeS} />
        : <TeamHeader game={game} awayS={awayS} homeS={homeS} />}
      {!isUFC && <RecentForm game={game} awayS={awayS} homeS={homeS} />}
      {!isUFC && h2h && <HeadToHead h2h={h2h} />}
      {(!isUFC || ufcHasStats) && (
        <TeamStats game={game} awayS={awayS} homeS={homeS} statDefs={statDefs} />
      )}
      {isUFC && !ufcHasStats && (
        <div style={{ textAlign:'center', padding:'14px 12px', fontSize:12, color:'var(--text-muted)', fontStyle:'italic', fontFamily:'Arial,sans-serif' }}>
          Detailed fight stats coming soon
        </div>
      )}
      {!isUFC && <InjuryReport game={game} awayS={awayS} homeS={homeS} />}
    </div>
  )
}
